import React, { useContext, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { equals, not } from "ramda";
import {
  firstError,
  hasError,
  validateAnthology,
  validateAnthologyField,
} from "../anthologyValidation";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";
import { anyTrue } from "../../../util/ext";
import { useBiblioboardUrl } from "../../common/BiblioboardLink";
import { MediaContext } from "../../common/media-context";
import { notBlank } from "../../../util/ext";

import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import EditColumn from "../../common/EditColumn";
import CopyIcon from "../../common/CopyIcon";
import ContentGroupOwner from "../../common/ContentGroupOwner";

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
  },

  charCount: {
    marginTop: 5,
    width: "100%",
    textAlign: "right",
  },

  column: {
    padding: "0 50px 0 50px",
  },
}));

const validator = {
  validateField: validateAnthologyField,
  validate: validateAnthology,
};

const EditAnthologyInfoColumn = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();
  const [url, showUrl] = useBiblioboardUrl(context.media);

  const [initial, setInitial] = useState(null);
  useEffect(() => {
    if (notBlank(context.initial) && not(equals(context.initial, initial))) {
      setInitial(context.initial);
    }
  }, [context, initial]);

  const formBuilder = handlers => {
    return (
      <div>
        <Grid container>
          <Grid
            className={classes.column}
            container
            item
            direction="column"
            spacing={4}
            xs={8}
          >
            <Grid item>
              <TextField
                id="anthology-name"
                fullWidth
                label="Title"
                value={context.media.name}
                onChange={handlers.onTextChange(["name"])}
                error={hasError(context.errors, "name")}
                helperText={firstError(context.errors, "name")}
                onBlur={() => handlers.onBlur("name", context.media.name)}
              />
            </Grid>
            <Grid item>
              <TextField
                id="anthology-subname"
                fullWidth
                label="Subtitle"
                value={context.media.subName}
                onChange={handlers.onTextChange(["subName"])}
                error={hasError(context.errors, "subName")}
                helperText={firstError(context.errors, "subName")}
                onBlur={() => handlers.onBlur("subName", context.media.subName)}
              />
            </Grid>
            {showUrl && (
              <Grid item>
                <TextField
                  id="anthology-biblioboard-url"
                  fullWidth
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIcon value={url} text="BiblioBoard URL" />
                      </InputAdornment>
                    ),
                  }}
                  label="BiblioBoard URL"
                  value={url}
                />
              </Grid>
            )}
            {anyTrue([isAdmin, isCreatorManager]) && (
              <Grid item>
                <TextField
                  id="anthology-internal-code"
                  fullWidth
                  maxLength={255}
                  label="Identifier (internal code)"
                  value={context.media.internalCode || ""}
                  onChange={handlers.onTextChange(["internalCode"])}
                  error={hasError(context.errors, "internalCode")}
                  helperText={firstError(context.errors, "internalCode")}
                  onBlur={() =>
                    handlers.onBlur("internalCode", context.media.internalCode)
                  }
                />
                <div className={classes.charCount}>
                  {context.media.internalCode?.length || 0}/255
                </div>
              </Grid>
            )}
          </Grid>
          <Grid container item direction="column" spacing={4} xs={4}>
            <Grid item>
              <TextField
                id="anthology-id"
                fullWidth
                InputProps={{
                  classes: { root: classes.name },
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyIcon value={context.media.id} text="Anthology ID" />
                    </InputAdornment>
                  ),
                }}
                label="Anthology ID"
                value={context.media.id}
                disabled
              />
            </Grid>

            <Grid item>
              <ContentGroupOwner />
            </Grid>

            <Grid item>
              <TextField
                id="anthology-keywords"
                fullWidth
                label="Keywords (optional)"
                value={context.media.keywords}
                onChange={handlers.onTextChange(["keywords"])}
                helperText="Keywords should be separated by commas"
              />
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <EditColumn
      className={classes.root}
      validator={validator}
      formBuilder={formBuilder}
    />
  );
};

export default EditAnthologyInfoColumn;
